import { Typography, Box, Button, Collapse, Alert, Autocomplete, TextField } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { CategorizedGeoJSON, FilterType, GeoFileType, GeoFileTypes, GroupedGeoJSON } from "../../../utilities/types";
import { FeatureCollection } from "geojson";
import { useEffect, useState, useMemo } from "react";
import { FOLDER_TOKEN_PREFIX } from "../../../utilities/constants";
import { FolderLevelsTable } from "./FolderLevelsTable";
import PropertyMappingSelector, { PropertyMappings } from "./PropertyMappingSelector";
import GeoTypeSelectorDropdown, { FilterParameters } from "./GeoTypeSelectorDropdown";
import GeoJsonPropertiesChips from "./GeoJsonPropertiesChips";
import { Close, MoreHoriz } from "@mui/icons-material";
import PortalTooltip from "../../helpers/PortalTooltip";

// Helper function to extract and normalize coordinates from a feature
function extractCoordinates(feature: GeoJSON.Feature): string {
	const geometry = feature.geometry;
	if (!geometry || geometry.type === "GeometryCollection") return "";

	// Type guard to ensure we have coordinates
	if (!("coordinates" in geometry)) return "";

	// Normalize coordinates by rounding to 6 decimal places to handle floating point precision
	const normalizeCoords = (coords: any): any => {
		if (Array.isArray(coords[0])) {
			return coords.map(normalizeCoords);
		}
		return coords.map((coord: number) => Math.round(coord * 1000000) / 1000000);
	};

	const normalizedCoords = normalizeCoords(geometry.coordinates);
	return JSON.stringify(normalizedCoords);
}

function matchExists(filter: FilterParameters, file: File, feature: GeoJSON.Feature) {
	// special case: We will count the condition as "True" if we didn't enter any other values
	// kind of an absolute true?
	if (!filter.filterKeys.length && !filter.matchType && !filter.value) {
		return true;
	}

	const matchFound = filter.filterKeys.some((key) => {
		let token = "";
		if (key.startsWith(FOLDER_TOKEN_PREFIX)) {
			const folderLevel = parseInt(key.split(" ")[2]);
			const tokens = file.name.split("/");
			token = tokens[folderLevel - 1];
		} else if (feature.properties && key in feature.properties) {
			token = feature.properties[key];
		}

		// console.log(key, token, filter.filterType, filter.matchType, filter.value);

		if (!token) {
			return false;
		}

		if (filter.matchType === "Exists") {
			return true;
		}

		// if not doing an "Exists" filter, and we don't have a value, then we will match this feature
		if (!filter.filterKeys.length) {
			return true;
		}

		// if we have keys but no filter, then we don't want to match
		if (!filter.value) {
			return false;
		}

		const result = token.toLowerCase().includes(filter.value.toLowerCase());

		if (filter.matchType === "Contains") {
			return result;
		} else if (filter.matchType === "Doesn't Contain") {
			return !result;
		}
	});
	return matchFound;
}

// Helper function to analyze geometry types in the data
export function analyzeGeometryTypes(shapeCounts: Record<string, number>): "polygons" | "points" | "mixed" | "none" {
	const geometryTypes = Object.keys(shapeCounts);

	if (geometryTypes.length === 0) {
		return "none";
	}

	const hasPolygons = geometryTypes.some((type) => type === "Polygon" || type === "MultiPolygon");
	const hasPoints = geometryTypes.some((type) => type === "Point");

	if (hasPolygons && hasPoints) {
		return "mixed";
	} else if (hasPolygons) {
		return "polygons";
	} else if (hasPoints) {
		return "points";
	} else {
		return "mixed"; // fallback for other geometry types
	}
}

const FilterTypeToGeoFileType: Record<FilterType, GeoFileType> = {
	Exclude: "Unknown",
	Boundary: "Boundary",
	Zones: "Zones",
	Centroids: "Centroids",
	Cores: "Cores",
};

function getFeatureCategory(file: File, feature: GeoJSON.Feature, categoryFilters: FilterParameters[]): GeoFileType {
	// // auto determine
	// if (feature.geometry.type === "Point") {
	//     return "Points";
	// }

	if (categoryFilters.length === 0) {
		return "Unknown";
	}

	for (const categoryFilter of categoryFilters) {
		if (!categoryFilter.filterType) {
			continue;
		}
		const matchFound = matchExists(categoryFilter, file, feature);
		if (matchFound) {
			return FilterTypeToGeoFileType[categoryFilter.filterType];
		}
		//return FilterTypeToGeoFileType[categoryFilter.filterType];
	}

	return "Unknown";
}

function isFeatureExcluded(excludeFilter: FilterParameters, file: File, feature: GeoJSON.Feature): boolean {
	return matchExists(excludeFilter, file, feature);
}

async function investigateGeoJSONData(
	geoDataList: GroupedGeoJSON[],
	filterMap: Record<number, FilterParameters> = {},
): Promise<CategorizedGeoJSON> {
	const results: Record<GeoFileType, Record<string, GeoJSON.FeatureCollection>> = {
		Boundary: {},
		Zones: {},
		Centroids: {},
		Cores: {},
		Unknown: {},
	};

	geoDataList.forEach((geoData) => {
		const geojson = { ...geoData.geoJSON }; // Create a copy to avoid mutating original

		const filters = Object.values(filterMap);
		const excludeFilters = filters.filter((filter) => filter.filterType === "Exclude");
		const categoryFilters = filters.filter((filter) => filter.filterType !== "Exclude");

		// Create a mock file object for compatibility with existing filter functions
		const mockFile = { name: geoData.groupKey } as File;

		geojson.features = geojson.features.filter((feature) => {
			return excludeFilters.every((filter) => !isFeatureExcluded(filter, mockFile, feature));
		});

		geojson.features.forEach((feature) => {
			if (excludeFilters.some((filter) => isFeatureExcluded(filter, mockFile, feature))) {
				return;
			}

			const featureType: GeoFileType = getFeatureCategory(mockFile, feature, categoryFilters);

			if (!results[featureType]) {
				results[featureType] = {};
			}

			if (!results[featureType][geoData.groupKey]) {
				results[featureType][geoData.groupKey] = {
					type: "FeatureCollection",
					features: [],
				};
			}
			results[featureType][geoData.groupKey].features.push(feature);
		});
	});

	const categorizedGeoJSON: CategorizedGeoJSON = {
		Boundary: [],
		Zones: [],
		Centroids: [],
		Cores: [],
		Unknown: [],
	};

	(Object.keys(results) as GeoFileType[]).forEach((type: GeoFileType) => {
		categorizedGeoJSON[type] = Object.values(results[type]);
	});

	return categorizedGeoJSON;
}

interface GeoTypeSelectorProps {
	index: number;
	inputData: GroupedGeoJSON[];
	options: string[];
	setOutputFiles(geoJsons: FeatureCollection[], type: GeoFileType): void;
	optionTooltips: string[][];
	onPropertyMappingsChange: (mappings: PropertyMappings) => void;
	applyTypeToGroups?: { type: FilterType; target: "polygons" | "points"; timestamp: number } | null;
	applyPropertyMapping?: { field: keyof PropertyMappings; value: string; timestamp: number } | null;
	groupKey?: string;
}

export function GeoTypeSelector(props: GeoTypeSelectorProps) {
	const [filterParameters, setFilterParameters] = useState<Record<number, FilterParameters>>({});
	const [propertyMappings, setPropertyMappings] = useState<PropertyMappings>({
		grower: null,
		farm: null,
		field: null,
		fieldId: null,
		uniqueKey: null,
	});
	const [detailsExpanded, setDetailsExpanded] = useState(false);
	const [showUniqueKeyDropdown, setShowUniqueKeyDropdown] = useState(false);

	const geoJsons = useMemo(() => {
		return props.inputData.map((data) => data.geoJSON).filter(Boolean);
	}, [props.inputData]);

	// Extract all properties from GeoJSON features with example values
	function extractAllPropertiesFromGeoData(geoJsons: any[]): Array<{ name: string; examples: string[] }> {
		const propertyMap = new Map<string, Set<string>>();

		// Collect unique values for each property
		for (const geoJson of geoJsons) {
			for (const feature of geoJson.features || []) {
				if (feature.properties) {
					Object.entries(feature.properties).forEach(([key, value]) => {
						if (!propertyMap.has(key)) {
							propertyMap.set(key, new Set<string>());
						}
						// Add the string representation of the value
						if (value !== null && value !== undefined && String(value).trim() !== "") {
							propertyMap.get(key)!.add(String(value));
						}
					});
				}
			}
		}

		// Convert to array format with up to 3 examples per property
		const result: Array<{ name: string; examples: string[] }> = [];
		for (const [propertyName, values] of propertyMap) {
			const examples = Array.from(values).slice(0, 3) as string[];
			result.push({ name: propertyName, examples });
		}

		// Sort by property name
		return result.sort((a, b) => a.name.localeCompare(b.name));
	}

	// Extract properties with examples from geoJsons
	const featureProperties = useMemo(() => {
		if (!geoJsons || geoJsons.length === 0) return [];
		return extractAllPropertiesFromGeoData(geoJsons);
	}, [geoJsons]);

	// Report property mappings changes to parent
	const handlePropertyMappingsChange = (mappings: PropertyMappings) => {
		setPropertyMappings(mappings);
		props.onPropertyMappingsChange(mappings);
	};

	// Handle unique key selection
	const handleUniqueKeyChange = (value: string | null) => {
		const updatedMappings = {
			...propertyMappings,
			uniqueKey: value,
		};
		setPropertyMappings(updatedMappings);
		props.onPropertyMappingsChange(updatedMappings);
	};

	// Calculate total shape counts from GeoJSON data
	const totalShapeCounts = useMemo(() => {
		const totals: Record<string, number> = {};

		props.inputData.forEach((geoData) => {
			if (geoData.geoJSON && geoData.geoJSON.features) {
				geoData.geoJSON.features.forEach((feature) => {
					const geometryType = feature.geometry.type;
					if (!totals[geometryType]) {
						totals[geometryType] = 0;
					}
					totals[geometryType]++;
				});
			}
		});

		return totals;
	}, [props.inputData]);

	// Detect duplicate geospatial data within this group
	const duplicateData = useMemo(() => {
		const coordinateMap = new Map<string, number>();

		props.inputData.forEach((geoData) => {
			geoData.geoJSON.features.forEach((feature) => {
				const coordStr = extractCoordinates(feature);
				if (coordStr) {
					coordinateMap.set(coordStr, (coordinateMap.get(coordStr) || 0) + 1);
				}
			});
		});

		// Find coordinates that appear more than once
		const duplicateCoords = Array.from(coordinateMap.entries()).filter(([, count]) => count > 1);

		const totalDuplicateFeatures = duplicateCoords.reduce((sum, [, count]) => sum + count, 0);

		return {
			hasDuplicates: duplicateCoords.length > 0,
			duplicateCount: duplicateCoords.length,
			totalDuplicateFeatures,
		};
	}, [props.inputData]);

	// Format shape counts for display
	const getShapeCountsDisplay = () => {
		const shapeTypes = Object.keys(totalShapeCounts);
		if (shapeTypes.length === 0) return null;
		return shapeTypes.map((type) => `${type}s: ${totalShapeCounts[type]}`).join(", ");
	};

	// Analyze geometry types to determine appropriate filter options
	const geometryAnalysis = useMemo(() => {
		return analyzeGeometryTypes(totalShapeCounts);
	}, [totalShapeCounts]);

	// Memoize options to prevent infinite re-renders
	const memoizedOptions = useMemo(() => {
		return props.options;
	}, [props.options.join(",")]);

	// Effect to handle button-triggered type application
	useEffect(() => {
		if (!props.applyTypeToGroups) return;

		const geometryType = analyzeGeometryTypes(totalShapeCounts);
		let shouldApply = false;

		if (props.applyTypeToGroups.target === "polygons" && (geometryType === "polygons" || geometryType === "mixed")) {
			shouldApply = true;
		} else if (props.applyTypeToGroups.target === "points" && geometryType === "points") {
			shouldApply = true;
		}

		if (shouldApply) {
			setFilterParameters({
				0: {
					filterType: props.applyTypeToGroups.type,
					filterKeys: [],
					matchType: null,
					value: "",
				},
			});
		}
	}, [
		props.applyTypeToGroups?.timestamp,
		props.applyTypeToGroups?.type,
		props.applyTypeToGroups?.target,
		totalShapeCounts,
	]);

	// Effect to handle global property mapping application
	useEffect(() => {
		if (!props.applyPropertyMapping) return;

		// Only apply if this group has Boundary filter type
		if (filterParameters[0]?.filterType !== "Boundary") return;

		const { field, value } = props.applyPropertyMapping;
		const updatedMappings = {
			...propertyMappings,
			[field]: value,
		};

		setPropertyMappings(updatedMappings);
		// Also notify parent of the change
		props.onPropertyMappingsChange(updatedMappings);
	}, [
		props.applyPropertyMapping?.timestamp,
		props.applyPropertyMapping?.field,
		props.applyPropertyMapping?.value,
		filterParameters,
		propertyMappings,
	]);

	useEffect(() => {
		// Don't process if there are no input files/data
		if (props.inputData.length === 0) {
			// Call setOutputFiles with empty arrays
			for (const type of GeoFileTypes) {
				props.setOutputFiles([], type);
			}
			return;
		}

		const processFiles = async () => {
			const categorizedGeoJSON = await investigateGeoJSONData(props.inputData, filterParameters);

			for (const type of GeoFileTypes) {
				props.setOutputFiles(categorizedGeoJSON[type], type);
			}
		};
		// Always process files when filter parameters change
		processFiles();
	}, [filterParameters, props.inputData, memoizedOptions]);

	const handleFilterChange = (newFilterParameters: React.SetStateAction<Record<number, FilterParameters>>) => {
		// Handle both direct values and updater functions
		const resolvedParams =
			typeof newFilterParameters === "function" ? newFilterParameters(filterParameters) : newFilterParameters;

		setFilterParameters(resolvedParams);
	};

	return (
		<>
			<Box display="flex" alignItems="center" justifyContent="space-between" paddingBottom={1}>
				<Typography variant="h5">{`Group ${props.index}`}</Typography>
				<Box display="flex" alignItems="center">
					{geometryAnalysis === "points" && (
						<Button
							onClick={() => setDetailsExpanded(!detailsExpanded)}
							startIcon={detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
							size="small"
						>
							{detailsExpanded ? "Hide Details" : "Show Details"}
						</Button>
					)}
					{geometryAnalysis !== "points" && (
						<PortalTooltip
							title={showUniqueKeyDropdown ? "Hide Advanced Options" : "Show Advanced Options"}
							placement="top"
						>
							<Button onClick={() => setShowUniqueKeyDropdown(!showUniqueKeyDropdown)} size="small" variant={"text"}>
								{showUniqueKeyDropdown ? <Close /> : <MoreHoriz />}
							</Button>
						</PortalTooltip>
					)}
				</Box>
			</Box>
			{getShapeCountsDisplay() && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					{getShapeCountsDisplay()}
				</Typography>
			)}

			{duplicateData.hasDuplicates && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					<Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
						Warning: Found {duplicateData.duplicateCount} sets of identical{" "}
						{geometryAnalysis === "points" ? "points" : geometryAnalysis === "polygons" ? "polygons" : "features"}.
					</Typography>
				</Alert>
			)}

			<GeoTypeSelectorDropdown
				key={`dropdown-${props.index}-${filterParameters[0]?.filterType || "none"}`}
				onFilterChange={handleFilterChange}
				geometryAnalysis={geometryAnalysis}
				globalType={filterParameters[0]?.filterType}
			/>
			{(geometryAnalysis === "mixed" || geometryAnalysis === "polygons") && (
				<>
					<GeoJsonPropertiesChips featureProperties={featureProperties} />
					<FolderLevelsTable data={props.inputData} truncate={3} />
				</>
			)}

			{geometryAnalysis === "points" && (
				<>
					<Collapse in={detailsExpanded}>
						<Box>
							<GeoJsonPropertiesChips featureProperties={featureProperties} />
							<FolderLevelsTable data={props.inputData} truncate={3} />
						</Box>
					</Collapse>
				</>
			)}

			{shouldShowPropertyMapper() && (
				<PropertyMappingSelector
					geoJsons={geoJsons}
					propertyMappings={propertyMappings}
					onMappingChange={handlePropertyMappingsChange}
					filterType={filterParameters[0]?.filterType}
				/>
			)}

			{/* Unique Key Dropdown */}
			<Collapse in={showUniqueKeyDropdown}>
				<br />
				<Typography variant="h6" gutterBottom>
					Advanced Options
				</Typography>
				<Box sx={{ mb: 2 }}>
					<Autocomplete
						options={featureProperties.map((prop) => prop.name)}
						value={propertyMappings.uniqueKey || null}
						onChange={(_, value) => handleUniqueKeyChange(value)}
						renderOption={(props, option) => {
							const property = featureProperties.find((p) => p.name === option);
							return (
								<Box component="li" {...props}>
									<Box>
										<Typography variant="body2">{option}</Typography>
										{property?.examples && property.examples.length > 0 && (
											<Typography variant="caption" color="text.secondary">
												Examples: {property.examples.slice(0, 2).join(", ")}
												{property.examples.length > 2 && "..."}
											</Typography>
										)}
									</Box>
								</Box>
							);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Select a property that defines a unique field"
								helperText="Defines a unique field. This property will be used to ensure that fields with multiple-polygon boundaries are handled properly. By default, we use grower+farm+field as the unique identifier."
								size="small"
							/>
						)}
					/>
				</Box>
			</Collapse>
		</>
	);

	function shouldShowPropertyMapper() {
		return geoJsons && geoJsons.length > 0 && filterParameters[0]?.filterType === "Boundary";
	}
}
